import React, { useState, useEffect, useCallback, useRef, memo } from 'react';
import '../style/Music.css';
import { IoSearchOutline } from 'react-icons/io5';
import { TiArrowShuffle } from 'react-icons/ti';
import { useNavigate } from 'react-router-dom';
import { IoIosArrowBack, IoMdCloseCircleOutline } from 'react-icons/io';
import Spinner from 'react-bootstrap/Spinner';
import { IoMusicalNotes } from 'react-icons/io5';
import { API_ENDPOINTS } from '../utils/constants';
import { fetchWithAuth } from './authService';

// Memoized Components
const SongItem = memo(({ song, onSongClick, isExcluded = false }) => (
  <button
    style={{
      border: 'none',
      background: 'transparent',
      padding: '2px',
      width: 'calc(100% / 3.5)',
      minWidth: 'calc(100% / 3.5)',
      maxWidth: 'calc(100% / 3.5)',
      height: 'calc(100% / 3.5)',
      position: 'relative',
      boxSizing: 'border-box',
      cursor: isExcluded ? 'not-allowed' : 'pointer',
      opacity: isExcluded ? 0.7 : 1,
    }}
    onClick={() => onSongClick(song)}
  >
    <img
      src={
        song?.album?.images?.find(img => img.width === 640)?.url ||
        song?.album?.images?.find(img => img.height === 640)?.url ||
        song?.album?.images?.[0]?.url
      }
      style={{
        width: '100%',
        borderRadius: '5px',
        filter: 'brightness(0.8)',
        display: 'block',
      }}
      alt={song?.name}
      loading="lazy"
    />
    <span
      style={{
        color: 'white',
        fontSize: 'clamp(0px, 3.2vw, 12px)',
        position: 'absolute',
        bottom: '8px',
        left: '8px',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        width: '85%',
        filter: 'brightness(2)',
        textAlign: 'left',
        fontWeight: 'bold',
      }}
    >
      {song?.name}
    </span>
  </button>
));
const SongItemTrending = memo(({ song, onSongClick, isExcluded = false }) => (
  <button
    className="col-4 position-relative"
    style={{
      border: 'none',
      background: 'transparent',
      padding: 2,
      cursor: isExcluded ? 'not-allowed' : 'pointer',
      opacity: isExcluded ? 0.7 : 1,
    }}
    onClick={() => onSongClick(song)}
  >
    <img
      className="w-100"
      src={
        song?.album?.images?.find(img => img.width === 640)?.url ||
        song?.album?.images?.find(img => img.height === 640)?.url ||
        song?.album?.images?.[0]?.url
      }
      style={{
        borderRadius: '5px',
        filter: 'brightness(0.8)',
      }}
      alt={song?.name}
      loading="lazy"
    />
    <span
      className="text-start"
      style={{
        color: 'white',
        fontSize: 'clamp(0px,3.2vw,12px)',
        position: 'absolute',
        bottom: 8,
        left: 8,
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        width: '85%',
        filter: 'brightness(2)',
      }}
    >
      <b>{song?.name}</b>
    </span>
  </button>
));
const CoinDisplay = memo(({ coins, onBuyClick }) => (
  <div
    className="text-end py-0"
    style={{
      position: 'relative',
      border: '1.321px solid #FED111',
      borderRadius: '20px',
      color: 'black',
      textDecoration: 'none',
      outline: 'none',
      cursor: 'pointer',
    }}
    onClick={onBuyClick}
  >
    <span
      className="text-end d-flex flex-row align-items-center"
      style={{
        color: 'black',
        borderColor: 'white',
        gap: '4px',
        paddingRight: '10px',
        paddingLeft: '0px',
      }}
    >
      <img src="/images/coin.svg" alt="Coin" style={{ height: '30px' }} />
      {typeof coins === 'number' ? coins : 0}
    </span>
  </div>
));

const ShimmerLoader = memo(({ count = 1 }) =>
  [...Array(count)].map((_, i) => (
    <div
      key={i}
      className="shimmer"
      style={{
        height: i === 0 ? '40px' : '',
        width: i === 0 ? '60px' : '30%',
        borderRadius: '7px',
      }}
    />
  ))
);

const ConfirmationModal = memo(
  ({ song, cost, onCancel, onConfirm, isShoutout = false }) => (
    <div className="modale">
      {/* Overlay */}
      <div
        className="overlay"
        onClick={onCancel}
        style={{
          position: 'fixed',
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0,0,0,0.5)',
          zIndex: 999,
        }}
      />

      {/* Modal content */}
      <div
        className="modale-content"
        style={{
          position: 'fixed',
          top: '40%', // slightly above center
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 1000,
          borderRadius: '15px',
          overflow: 'hidden',
          maxWidth: '90%',
          background: '#fff',
        }}
      >
        {/* Header */}
        <div
          className="w-100 p-3 d-flex flex-row"
          style={{
            borderRadius: '15px 15px 0px 0px',
            background: 'linear-gradient(90deg, #C91721 0%, #8A325D 100%)',
          }}
        >
          <div className="col-3" />
          <div className="col-9 d-flex flex-row justify-content-between">
            <h4 className="m-0" style={{ color: 'white' }}>
              Song Request
            </h4>
            <button
              style={{
                color: 'white',
                background: 'transparent',
                border: 'none',
              }}
              onClick={onCancel}
            >
              <IoMdCloseCircleOutline
                style={{ color: 'white', fontSize: '25px' }}
              />
            </button>
          </div>
        </div>

        {/* Body */}
        <div
          className="px-3 w-100"
          style={{ background: '#FFF', padding: '15px 0px 15px 0px' }}
        >
          <div className="d-flex flex-row justify-content-between align-items-center">
            <div
              className="d-flex flex-row align-items-center"
              style={{ gap: '5px' }}
            >
              <img
                src={song?.album?.images[0]?.url}
                alt="Album Cover"
                style={{
                  height: '60px',
                  width: '60px',
                  borderRadius: '50%',
                }}
              />
              <span
                style={{
                  color: '#9F2A4A',
                  fontSize: 'clamp(0px,4vw,16px)',
                }}
                className="text-start col-8"
              >
                {song?.name}
              </span>
            </div>

            {!isShoutout && (
              <div>
                <img
                  src="/images/coin.svg"
                  alt="Coin"
                  style={{
                    width: 'clamp(0px,5vw,20px)',
                    aspectRatio: '1/1',
                  }}
                />
                <h4
                  style={{
                    color: '#9F2A4A',
                    fontSize: 'clamp(0px,4vw,16px)',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {cost} {cost > 0 ? 'coins' : 'coin'}
                </h4>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="d-flex flex-row justify-content-between align-items-center w-100">
          <button
            className="col-6 p-2"
            style={{
              background: '#FFF',
              color: '#C71823',
              border: 'solid .5px #C71823',
              borderRadius: '0px 0px 0px 15px',
              fontSize: '18px',
            }}
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            className="col-6 p-2"
            style={{
              background:
                'linear-gradient(90deg, #CC151E -18.35%, #873662 108.23%)',
              color: 'white',
              border: 'solid .5px #C71823',
              borderRadius: '0px 0px 15px 0px',
              fontSize: '18px',
            }}
            onClick={onConfirm}
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  )
);

const Music = () => {
  // State declarations
  const [MOptionInd, setMOptionInd] = useState(1);
  const [artists, setArtists] = useState([]);
  const [trendings, setTrendings] = useState([]);
  const [displayedSongs, setDisplayedSongs] = useState([]);
  const [profile, setProfile] = useState({ coins: 0, username: '' });
  const [playlists, setPlaylists] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [specificSongs, setSpecificSongs] = useState([]);
  const [confirmModal, setConfirmModal] = useState(false);
  const [songCost, setSongCost] = useState(null);
  const [songToAdd, setSongToAdd] = useState(null);
  const [VName, setVName] = useState(null);
  const [djDetails, setDjDetails] = useState(localStorage.getItem('vId'));

  const [errorMsg, setErrorMsg] = useState(null);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isProfileLoading, setIsProfileLoading] = useState(true);

  const navigate = useNavigate();
  const errorTimeoutRef = useRef(null);
  const abortControllerRef = useRef(null);
  const [skip, setSkip] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const loaderRef = useRef(null);
  // Excluded labels setup (same as Search)
  const excludedLabelsRaw = localStorage.getItem('excludedLabels');
  let excludedLabels = [];
  if (excludedLabelsRaw) {
    try {
      excludedLabels = JSON.parse(excludedLabelsRaw);
    } catch {
      excludedLabels = excludedLabelsRaw.split(',').map(l => l.trim());
    }
  }

  // Cancel message
  const cancelMsg = useCallback(() => {
    setIsError(false);
    setErrorMsg(null);
  }, []);

  // Navigation handlers
  const clickOnVenue = useCallback(
    vId => {
      if (!vId) return;
      navigate(`/venues/${vId}`);
    },
    [navigate]
  );

  const navigateAlbum = useCallback(
    (id, url, name) => {
      if (url) localStorage.setItem('playlistImg', url);
      if (name) localStorage.setItem('playlistname', name);
      navigate(`/playlist?pid=${id}`);
    },
    [navigate]
  );

  // Fetch profile with normal fetch
  const fetchProfile = useCallback(async () => {
    setIsProfileLoading(true);
    const token = localStorage.getItem('tringAuthToken');
    if (!token) {
      navigate('/');
      return;
    }

    try {
      const response = await fetchWithAuth(API_ENDPOINTS.USERS);

      if (!response.ok) throw new Error('Failed to fetch data');

      const data = await response.json();
      setProfile({
        coins: data?.coins ?? 0,
        username: data?.username ?? '',
      });
    } catch (error) {
      console.error('Error fetching profile:', error);
      setProfile({ coins: 0, username: '' });
      setErrorMsg('Failed to load profile');
      setIsError(true);
    } finally {
      setIsProfileLoading(false);
    }
  }, [navigate]);

  // Handle language option selection
  const handleOption = useCallback(
    async index => {
      setMOptionInd(index);
      if (index === 1) return;

      const token = localStorage.getItem('tringAuthToken');
      if (!token) {
        navigate('/');
        return;
      }

      try {
        const response = await fetchWithAuth(
          `${API_ENDPOINTS.PLAYLIST_TRACKS(languages[index - 2]?.id)}?limit=100`
        );

        if (!response.ok) throw new Error('Failed to fetch songs');

        const data = await response.json();
        setSpecificSongs(data || []);
      } catch (error) {
        console.error('Error fetching songs:', error);
        setErrorMsg('Failed to load songs');
        setIsError(true);
      }
    },
    [languages, navigate]
  );

  const loadPlaylists = useCallback(async () => {
    if (!hasMore || isLoading) return; // Skip if already loading or no more data
    if (skip == 0) setIsLoading(true);
    try {
      const response = await fetchWithAuth(
        `${API_ENDPOINTS.PLAYLISTS}?skip=${skip}&limit=7&venueId=${djDetails}`,
        {
          method: 'GET',
        }
      );

      if (!response.ok) throw new Error('Failed to fetch playlists');

      const data = await response.json();
      if (data.length < 7) setHasMore(false); // Stop if less than limit

      const enrichedPlaylists = await Promise.all(
        data.map(async playlist => {
          const tracksResponse = await fetchWithAuth(
            API_ENDPOINTS.PLAYLIST_TRACKS(playlist.id),
            {
              method: 'GET',
            }
          );
          if (!tracksResponse.ok)
            throw new Error('Failed to fetch playlist tracks');
          return {
            ...playlist,
            songs: await tracksResponse.json(),
          };
        })
      );

      setPlaylists(prev => {
        const existingIds = new Set(prev.map(p => p.id));
        const uniqueNew = enrichedPlaylists.filter(p => !existingIds.has(p.id));
        return [...prev, ...uniqueNew];
      });
      setSkip(prev => prev + 7);
    } catch (error) {
      console.error('Error fetching playlists:', error);
      setErrorMsg('Failed to load playlists');
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  }, [djDetails, hasMore, isLoading, skip]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && !isLoading && hasMore) {
          loadPlaylists();
        }
      },
      { threshold: 0.1 }
    );

    if (loaderRef.current) observer.observe(loaderRef.current);
    return () => observer.disconnect();
  }, [isLoading, hasMore, loadPlaylists]);
  // Initialize data
  useEffect(() => {
    const initializeData = async () => {
      setIsLoading(true); // 🔹 Start loading
      try {
        setDjDetails(localStorage.getItem('vId'));
        setVName(localStorage.getItem('venueName'));

        // Fetch all required data directly (no cache/localStorage check)
        const [trendingData, artistsData, languagesData] = await Promise.all([
          // Trending songs
          (async () => {
            const response = await fetchWithAuth(
              `${API_ENDPOINTS.TRACK_TRENDING}?venueId=${djDetails}`
            );
            if (!response.ok) throw new Error('Failed to fetch trending');
            return response.json();
          })(),

          // Artists
          (async () => {
            const response = await fetchWithAuth(API_ENDPOINTS.ARTISTS);
            if (!response.ok) throw new Error('Failed to fetch artists');
            const data = await response.json();
            return data.artists;
          })(),

          // Languages
          (async () => {
            const response = await fetchWithAuth(
              `${API_ENDPOINTS.LANGUAGES}?venueId=${djDetails}`
            );
            if (!response.ok) throw new Error('Failed to fetch languages');
            const data = await response.json();
            return data.languages;
          })(),
        ]);

        // Set states
        setTrendings(trendingData);
        setDisplayedSongs(trendingData.slice(0, 6));
        setArtists(artistsData);
        setLanguages(languagesData);
      } catch (error) {
        console.error('Error initializing data:', error);
        setErrorMsg('Failed to load initial data');
        setIsError(true);
      } finally {
        setIsLoading(false); // 🔹 Stop loading
      }
    };

    initializeData();
  }, [navigate]);

  // Error message timeout
  useEffect(() => {
    if (isError) {
      clearTimeout(errorTimeoutRef.current);
      errorTimeoutRef.current = setTimeout(cancelMsg, 4000);
    }
    return () => clearTimeout(errorTimeoutRef.current);
  }, [isError, cancelMsg]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);
  const [price, setPrice] = useState([]);
  const fetchPrice = async () => {
    try {
      const response = await fetchWithAuth(
        API_ENDPOINTS.ORDER_PRICE_BY_VENUE(localStorage.getItem('vId'))
      );
      if (!response.ok) throw new Error('Failed to fetch price');

      const data = await response.json();
      // Filter out hidden packs
      const visiblePacks = data.filter(pack => !pack.isHidden);
      setPrice(visiblePacks);
    } catch (error) {
      console.error('Error fetching prices:', error);
    }
  };
  // Load profile
  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);
  useEffect(() => {
    fetchPrice();
  }, []);
  // Song handling
  const openConfirm = useCallback(
    async song => {
      const venueId = localStorage.getItem('vId');
      const cost = localStorage.getItem('songCost');

      if (!venueId) {
        setIsError(true);
        setErrorMsg('No Venue Selected..');
        return;
      }

      if (!cost || djDetails === null) {
        setErrorMsg('Invalid song cost or DJ details');
        setIsError(true);
        return;
      }

      const isKJ = localStorage.getItem('isKJ');
      if (isKJ === 'true') {
        const venueId = localStorage.getItem('venueId');

        try {
          const res = await fetch(
            API_ENDPOINTS.VENUE_SLOTS_AVAILABILITY(venueId)
          );

          if (!res.ok) {
            setIsError(true);
            setErrorMsg('Failed to check song slot availability.');
            return;
          }

          const data = await res.json();
          if (!data.available) {
            setErrorMsg('No more song slots available!');
            setIsError(true);
            return;
          }
        } catch (error) {
          console.error('Error fetching data:', error);
          setIsError(true);
          setErrorMsg('Network error occurred. Please try again.');
          return;
        }
      }

      setSongCost(cost);
      setSongToAdd(song);
      console.log('song', song);
      const currentCoins = profile?.coins ?? 0;

      if (currentCoins < Number(cost)) {
        navigate('/insufficient-coins', {
          state: {
            song,
            cost,
            priceData: price,
            walletCoins: currentCoins,
          },
        });
      } else {
        setConfirmModal(true);
      }
    },
    [djDetails, profile]
  );
  const onAddSong = useCallback(
    async (trackId, songName) => {
      setIsLoading(true);
      setErrorMsg(''); // Clear previous errors
      setIsError(false);

      try {
        // Validate token
        const token = localStorage.getItem('tringAuthToken');
        if (!token) {
          throw new Error('Authentication required. Please log in.');
        }

        // Validate venue ID
        const venueId = localStorage.getItem('vId');
        if (!venueId) {
          throw new Error('Please select a venue first.');
        }

        // Handle shoutout case
        if (localStorage.getItem('shoutout') === 'true') {
          localStorage.setItem('shouttrackId', trackId);
          navigate('/shoutout');
          return;
        }

        // First POST request with error handling
        const userResponse = await fetchWithAuth(
          API_ENDPOINTS.VENUE_USERS(venueId),
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({}),
          }
        );

        if (!userResponse.ok) {
          const errorData = await userResponse.json().catch(() => ({}));
          throw new Error(
            errorData.message || 'Failed to register user with venue'
          );
        }

        // Second POST request with error handling
        const queueResponse = await fetchWithAuth(
          API_ENDPOINTS.VENUE_QUEUE(venueId),
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ trackId }),
          }
        );

        const queueData = await queueResponse.json().catch(() => ({}));

        if (!queueResponse.ok) {
          throw new Error(queueData.message || 'Failed to add song to queue');
        }
        // Success case
        localStorage.setItem('notif', true);
        navigate(`/venues/${venueId}`);
      } catch (error) {
        console.error('Error in onAddSong:', error);
        setErrorMsg(error.message || 'An unexpected error occurred');
        setIsError(true);

        // Handle specific error cases
        if (error.message.includes('Authentication')) {
          navigate('/');
        }
      } finally {
        setIsLoading(false);
        setConfirmModal(false);
        setSongToAdd(null);
      }
    },
    [navigate]
  );

  // Shuffle songs
  const shuffleSongs = useCallback(() => {
    if (!trendings?.length) return;
    const shuffled = [...trendings].sort(() => 0.5 - Math.random()).slice(0, 6);
    setDisplayedSongs(shuffled);
  }, [trendings]);

  // Render song list
  const renderSongList = useCallback(
    songs => {
      if (!songs?.length) {
        return <ShimmerLoader count={20} />;
      }

      return songs.map(song => {
        const isExcluded = song?.label && excludedLabels?.includes(song.label);
        const handleClick = s => {
          if (isExcluded) {
            setErrorMsg('This song is not available in this venue.');
            setIsError(true);
            return;
          }
          openConfirm(s);
        };
        return (
          <SongItem
            key={song.id}
            song={song}
            onSongClick={handleClick}
            isExcluded={isExcluded}
          />
        );
      });
    },
    [openConfirm, excludedLabels]
  );
  const renderTrendingSongList = useCallback(
    songs => {
      if (!songs?.length) {
        return <ShimmerLoader count={20} />;
      }

      return songs.map(song => {
        const isExcluded = song?.label && excludedLabels?.includes(song.label);
        const handleClick = s => {
          if (isExcluded) {
            setErrorMsg('This song is not available in this venue.');
            setIsError(true);
            return;
          }
          openConfirm(s);
        };
        return (
          <SongItemTrending
            key={song.id}
            song={song}
            onSongClick={handleClick}
            isExcluded={isExcluded}
          />
        );
      });
    },
    [openConfirm, excludedLabels]
  );
  return (
    <div
      className="music-container px-2 position-relative"
      style={{ overflow: 'scroll', scrollBehavior: 'smooth' }}
    >
      {/* Modals */}
      {confirmModal && (
        <ConfirmationModal
          song={songToAdd}
          cost={songCost}
          onCancel={() => {
            setSongToAdd(null);
            setConfirmModal(false);
          }}
          onConfirm={() => onAddSong(songToAdd.id, songToAdd.name)}
          isShoutout={localStorage.getItem('shoutout') === 'true'}
        />
      )}

      {/* Error Message */}
      {isError && (
        <div
          style={{
            position: 'fixed',
            bottom: '80px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 10000,
            color: '#FFFFFF',
            background: 'rgb(32, 32, 32)',
            borderRadius: '4px',
            boxShadow: '0 4px 12px rgba(32, 32, 32, 0.73)',
            padding: '16px 24px',
            minWidth: '95vw',
            maxWidth: '95vw',
            animation: 'slideDown 0.4s cubic-bezier(0.18, 0.89, 0.32, 1.28)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px',
          }}
        >
          <span
            style={{
              flex: 1,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              textAlign: 'start',
            }}
          >
            {typeof errorMsg === 'string'
              ? errorMsg
              : errorMsg?.message || 'Something went wrong'}
          </span>
          {errorMsg === 'No Venue Selected..' && (
            <button
              style={{
                flexShrink: 0,
                padding: '4px 8px',
                backgroundColor: 'rgb(211, 64, 64)',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                fontSize: '13px',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
              onClick={() => {
                localStorage.setItem('mainInd', 1);
                window.location.reload();
              }}
            >
              Select Venue
            </button>
          )}
        </div>
      )}

      {/* Loading Spinner */}
      {isLoading && (
        <div className="lmodale">
          <div className="loverlay" />
          <div className="lmodalecontent">
            <Spinner animation="border" role="status" style={{ color: 'red' }}>
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="d-flex w-100 flex-column align-items-center h-100">
        {/* Header */}
        <div
          className="d-flex w-100 flex-column align-items-center"
          style={{ background: 'white' }}
        >
          <div className="d-flex flex-row justify-content-between align-items-center mt-2 mb-2 w-100">
            {/* Venue Info */}
            <div
              className="d-flex flex-row align-items-center"
              style={{ flex: 1, minWidth: 0 }} // allow shrinking if needed
              onClick={() => djDetails !== null && clickOnVenue(djDetails)}
            >
              <IoIosArrowBack
                style={{
                  fontSize: '35px',
                  marginRight: '7px',
                  marginLeft: '-5px',
                  color: '#9C2C4D',
                }}
                onClick={e => {
                  e.stopPropagation();
                  const venueId = djDetails || localStorage.getItem('vId');
                  if (venueId) {
                    navigate(`/venues/${venueId}`);
                  } else {
                    navigate(-1);
                  }
                }}
              />

              <div
                className="text-start h-100"
                style={{
                  flexGrow: 1,
                  overflow: 'hidden',
                  paddingRight: '10px', // optional spacing from coins
                }}
              >
                <span
                  className="common-txt"
                  style={{
                    fontSize: '13px',
                    fontWeight: '400',
                    marginLeft: '2px',
                  }}
                >
                  Now Playing at <br />
                </span>
                <span
                  className="common-txt"
                  style={{
                    fontSize: 'clamp(0px,4.5vw,18px)',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    color: '#9C2C4D',
                    display: 'inline-block',
                    maxWidth: '100%',
                    cursor: 'pointer',
                    fontWeight: '500',
                  }}
                  onClick={() => {
                    if (!VName) {
                      localStorage.setItem('mainInd', 1);
                      window.location.reload();
                    }
                  }}
                >
                  {VName ? VName : 'Select Venue'}
                </span>
              </div>
            </div>

            {/* Coins Display */}
            <CoinDisplay
              coins={profile?.coins ?? 0}
              onBuyClick={() => navigate('/buy')}
            />
          </div>

          {/* Search Bar */}
          <div className="music-search-container mt-0 mb-2 w-100">
            <button
              onClick={() => navigate('/searchSong')}
              style={{
                border: '0.5px solid rgb(208, 208, 208)',
                background: 'transparent',
                fontWeight: 400,
                fontSize: '20px',
                width: '100%',
                textAlign: 'left',
                padding: '0.25rem',
              }}
              className="music-search-input p-1 text-start w-100"
            >
              <IoSearchOutline className="mb-1" /> Search for a Song...
            </button>
          </div>
        </div>

        {/* Content Area */}
        <div
          className="w-100 align-items-center mb-1"
          style={{ flexGrow: 1, overflow: 'scroll', scrollBehavior: 'smooth' }}
        >
          {/* Language Options */}
          <div
            className="w-100 music-options d-flex flex-row justify-content-start overflow-scroll py-1"
            style={{ gap: '15px', scrollBehavior: 'smooth' }}
          >
            <button
              className="music-option px-3 py-1"
              style={{
                background:
                  MOptionInd === 1
                    ? 'linear-gradient(90deg, #DB0F11 0%, #863663 100%)'
                    : 'white',
                color: MOptionInd === 1 ? 'white' : '#d7080c',
                borderRadius: '7px',
                fontSize: 'clamp(0px,4vw,18px)',
                display: languages?.length ? 'block' : 'none',
              }}
              onClick={() => handleOption(1)}
            >
              All
            </button>

            {languages?.length ? (
              <div className="d-flex flex-row" style={{ gap: '15px' }}>
                {languages.map((item, index) => (
                  <button
                    key={item.id}
                    className="music-option px-3 py-1"
                    onClick={() => handleOption(index + 2)}
                    style={{
                      background:
                        MOptionInd === index + 2
                          ? 'linear-gradient(90deg, #d7080c 0%, #863663 100%)'
                          : 'white',
                      color: MOptionInd === index + 2 ? 'white' : '#d7080c',
                      borderRadius: '7px',
                      fontSize: 'clamp(0px,4vw,18px)',
                    }}
                  >
                    {item?.language}
                  </button>
                ))}
              </div>
            ) : (
              <div
                className="d-flex flex-row w-100 justify-content-between"
                style={{ gap: '5px' }}
              >
                <ShimmerLoader count={5} />
              </div>
            )}
          </div>

          {/* Songs Display */}
          {MOptionInd !== 1 ? (
            <div>
              <h5 className="w-100 text-start mt-3">
                {languages[MOptionInd - 2]?.name}
              </h5>
              <div
                className="d-flex flex-wrap w-100 overflow-scroll"
                style={{ scrollBehavior: 'smooth' }}
              >
                {renderTrendingSongList(specificSongs)}
              </div>
            </div>
          ) : (
            <div className="mb-5">
              <div className="mt-2">
                {/* Trending Section */}
                <div className="d-flex flex-row justify-content-between align-items-center mb-1">
                  <h6
                    className="m-0"
                    style={{ fontSize: 'clamp(0px,4vw,18px)' }}
                  >
                    Trending Now
                  </h6>
                  <button
                    className="Shuffle-btn px-2"
                    style={{
                      background:
                        'linear-gradient(90deg, #DB0F11 0%, #863663 100%)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '5px',
                      fontSize: 'clamp(0px,3vw,10px)',
                      padding: 2,
                    }}
                    onClick={shuffleSongs}
                  >
                    Shuffle <TiArrowShuffle className="mb-1" />
                  </button>
                </div>

                {/* Trending Songs */}
                <div
                  className="d-flex flex-wrap w-100 overflow-scroll"
                  style={{ scrollBehavior: 'smooth', gap: 0 }}
                >
                  {renderTrendingSongList(displayedSongs)}
                </div>
              </div>

              {/* Playlists */}
              {playlists.slice(1).map(
                playlist =>
                  playlist?.songs?.length > 0 && ( // ✅ only show if songs exist
                    <div key={playlist.id} className="mt-2">
                      <div className="d-flex flex-row justify-content-between align-items-center">
                        <h5
                          className="m-0 text-start"
                          style={{
                            whiteSpace: 'nowrap',
                            fontSize: 'clamp(0px,4vw,18px)',
                          }}
                        >
                          {playlist?.name || 'Playlist'}
                        </h5>
                        <button
                          className="Shuffle-btn common-txt"
                          style={{
                            background: 'transparent',
                            border: 'none',
                            whiteSpace: 'nowrap',
                          }}
                          onClick={() =>
                            navigateAlbum(
                              playlist.id,
                              playlist?.images[0]?.url,
                              playlist?.name
                            )
                          }
                        >
                          See all
                        </button>
                      </div>

                      <div
                        className="d-flex flex-row mb-0 align-items-start w-100 overflow-scroll"
                        style={{
                          marginBottom: '100px',
                          gap: '2px',
                          whiteSpace: 'nowrap',
                          scrollBehavior: 'smooth',
                        }}
                      >
                        {renderSongList(playlist.songs)}
                      </div>
                    </div>
                  )
              )}

              {hasMore && (
                <div
                  ref={loaderRef}
                  style={{ height: '10px', width: '100%' }}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default memo(Music);
